import React, { useMemo } from 'react';

import { Form, Select } from 'antd';
import groupBy from 'lodash/groupBy';
import { FontWeight } from '@common/tokens';
import { SpearfishableWorkersByShiftGapQueryCgResponse } from '@common/graphql/schema';

export const IndividualRecipientsSelector = ({
  workerIds,
  setWorkerIds,
  individualSpearfishableWorkerData,
}: {
  workerIds: string[];
  setWorkerIds: (workerIds: string[]) => void;
  individualSpearfishableWorkerData:
    | SpearfishableWorkersByShiftGapQueryCgResponse
    | undefined;
}) => {
  const groupedWorkers = useMemo(() => {
    const workers =
      individualSpearfishableWorkerData?.spearfishableWorkersByShiftGap || [];

    return groupBy(workers, (sw) => sw.worker?.workforceGroup?.name);
  }, [individualSpearfishableWorkerData?.spearfishableWorkersByShiftGap]);

  const transformedWorkerData = useMemo(
    () =>
      Object.keys(groupedWorkers).map((groupName) => {
        const workersInGroup = groupedWorkers[groupName];

        const sortedByName = [...workersInGroup].sort((a, b) => {
          const nameA = a.worker.worksUser.name.toLowerCase();
          const nameB = b.worker.worksUser.name.toLowerCase();
          if (nameA < nameB) {
            return -1;
          }
          return 1;
        });

        const options = sortedByName.map((sw) => ({
          label: (
            <div className="wk-individual-recipients-selector-label">
              <span className="worker-label">{sw.worker.worksUser.name}</span>
              <span className="worker-detail">{sw.spearfishingDetail}</span>
            </div>
          ),
          // to use on search
          workerName: sw.worker.worksUser.name,
          value: sw.worker.id,
          disabled: !sw.spearfishable,
        }));

        return {
          label: groupName,
          options: options,
        };
      }),
    [groupedWorkers]
  );

  return (
    <Form.Item
      label="Recipients"
      name="workers"
      className="wk-individual-recipients-selector-form-item sentry-mask"
      required
      rules={[
        {
          required: false,
          message: 'Please select at least one worker',
        },
      ]}
    >
      <Select
        mode="multiple"
        filterOption={(input, option) =>
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          (option?.workerName ?? '').toLowerCase().includes(input.toLowerCase())
        }
        onChange={(e) => {
          setWorkerIds(e as string[]);
        }}
        className="wk-individual-recipients-selector-select-input"
        showSearch
        value={workerIds}
        options={transformedWorkerData}
        notFoundContent={<div>No workers found</div>}
        placeholder="Search available staff"
      />
      <style jsx>{`
        :global(.wk-individual-recipients-selector-label) {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        :global(.wk-individual-recipients-selector-form-item) {
          font-weight: ${FontWeight.medium};
          margin-bottom: 24px;
        }

        :global(.wk-individual-recipients-selector-select-input) {
          width: 100%;
        }
      `}</style>
    </Form.Item>
  );
};
